import type {
  PreviewSimPersona,
  PreviewSimRunStatus,
  PreviewSimTurnStat,
} from "@/api/previewSim";

/**
 * Simulated-patient runs: the small pure parts the surfaces share.
 *
 * Every number and string here is the target's. Nothing is derived clinically,
 * and no transcript text exists on this surface: the conversation itself is
 * read through Preview Insights.
 */

export const SIM_LANGUAGES = ["he", "en"] as const;
export type SimLanguage = (typeof SIM_LANGUAGES)[number];

export const SIM_LANGUAGE_LABELS = {
  he: "Hebrew",
  en: "English",
} as const satisfies Record<SimLanguage, string>;

/** The service's bounds on the turn budget (`maxTurns`), restated for the form. */
export const SIM_MIN_TURNS = 1;
export const SIM_MAX_TURNS = 150;
export const SIM_DEFAULT_TURNS = 150;

/** Personas the chosen language can run, in the order the target listed them. */
export function personasFor(
  personas: readonly PreviewSimPersona[],
  language: SimLanguage,
): PreviewSimPersona[] {
  return personas.filter((persona) => persona.language === language);
}

/**
 * The turn budget the field holds, or null when it is not a whole number in
 * the service's range — which is what disables the run control rather than
 * letting the service answer a 422 about a value the form could have named.
 */
export function readMaxTurns(text: string): number | null {
  if (!/^\d+$/.test(text.trim())) {
    return null;
  }
  const value = Number(text.trim());
  return value >= SIM_MIN_TURNS && value <= SIM_MAX_TURNS ? value : null;
}

export const SIM_STATUS_LABELS = {
  queued: "Queued",
  running: "Running",
  completed: "Completed",
  failed: "Failed",
} as const satisfies Record<PreviewSimRunStatus["status"], string>;

/**
 * The end reasons the contract names, in words. `endReason` is an open string
 * on the wire, so anything else is shown as the target wrote it.
 */
const END_REASON_LABELS: Record<string, string> = {
  maxTurns: "Reached the turn budget",
  brainEnded: "The conversation ended itself",
  simEnded: "The simulated patient ended the conversation",
  error: "Stopped by an error",
};

export function describeEndReason(reason: string | null | undefined): string {
  if (reason === null || reason === undefined || reason === "") {
    return "Not ended yet";
  }
  return END_REASON_LABELS[reason] ?? reason;
}

/** Whole-run figures over the per-turn stats; counts are null when no turn reported one. */
export type SimStatTotals = {
  turns: number;
  meanLatencyMs: number | null;
  promptTokens: number | null;
  completionTokens: number | null;
  models: readonly string[];
};

function sumOrNull(
  values: readonly (number | null | undefined)[],
): number | null {
  const present = values.filter(
    (value): value is number => typeof value === "number",
  );
  return present.length === 0
    ? null
    : present.reduce((total, value) => total + value, 0);
}

export function totalSimStats(
  stats: readonly PreviewSimTurnStat[],
): SimStatTotals {
  return {
    turns: stats.length,
    meanLatencyMs:
      stats.length === 0
        ? null
        : Math.round(
            stats.reduce((total, stat) => total + stat.latencyMs, 0) /
              stats.length,
          ),
    promptTokens: sumOrNull(stats.map((stat) => stat.promptTokens)),
    completionTokens: sumOrNull(stats.map((stat) => stat.completionTokens)),
    models: [...new Set(stats.map((stat) => stat.model))],
  };
}
